import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
	Search as SearchIcon,
	Grid,
	List,
	ChevronDown,
	AlertCircle,
	Sparkles,
	TrendingUp,
} from 'lucide-react'

import Layout from '@/components/layout/layout'
import Toast from '@/components/elements/toast'
import ProductCardSearch from '@/components/cards/product-card-search'
import {
	FilteredProductsResponse,
	AppliedFilters,
} from '@/entities/api/products/product-filters'
import {
	advancedFilter,
	intelligentSearch,
	quickSearch,
} from '@/services/api/products/products-filter'
import { ProductCardSkeletons } from '@/components/cards/product-card-search-skeleton'
import ProductSimpleSlider from '@/components/sliders/products-simple-slider'
import CustomersEntity from '@/entities/api/customers/customer'

type SortOption =
	| 'relevance'
	| 'price-asc'
	| 'price-desc'
	| 'name'
	| 'newest'
	| 'rating'
type ViewMode = 'grid' | 'list'

interface SortConfig {
	value: SortOption
	label: string
}

const Search = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const [products, setProducts] = useState<FilteredProductsResponse | null>(
		null
	)
	const [productsMore, setProductsMore] =
		useState<FilteredProductsResponse | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [viewMode, setViewMode] = useState<ViewMode>('list')
	const [sortBy, setSortBy] = useState<SortOption>('relevance')
	const [currentPage] = useState(1)
	const [morePage, setMorePage] = useState(1)
	const [hasMoreProducts, setHasMoreProducts] = useState(true)

	const abortControllerRef = useRef<AbortController | null>(null)
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const lastSearchRef = useRef<string>('')

	const query = searchParams.get('q') || ''
	const categoryId = searchParams.get('category')
	const minPrice = searchParams.get('minPrice')
	const maxPrice = searchParams.get('maxPrice')

	const sortOptions: SortConfig[] = useMemo(
		() => [
			{ value: 'relevance', label: 'Más relevantes' },
			{ value: 'price-asc', label: 'Menor precio' },
			{ value: 'price-desc', label: 'Mayor precio' },
			{ value: 'name', label: 'Nombre A-Z' },
			{ value: 'newest', label: 'Más recientes' },
			{ value: 'rating', label: 'Mejor valorados' },
		],
		[]
	)

	const searchKey = useMemo(
		() => `${query}-${categoryId}-${minPrice}-${maxPrice}-${currentPage}`,
		[query, categoryId, minPrice, maxPrice, currentPage]
	)

	const cancelPreviousRequest = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
		}
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}
	}, [])

	const loadMoreProducts = useCallback(async () => {
		if (isLoadingMore || !hasMoreProducts) return

		try {
			setIsLoadingMore(true)

			const response = await advancedFilter({
				limit: 10,
				page: morePage + 1,
			})

			if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
				Toast({ type: 'error', message: 'No se pudieron cargar más productos' })
				return
			}

			const newData = response?.data
			if (newData && newData.products.length > 0) {
				if (productsMore) {
					setProductsMore({
						...newData,
						products: [...productsMore.products, ...newData.products],
					})
				} else {
					setProductsMore(newData)
				}

				setMorePage(morePage + 1)
				setHasMoreProducts(
					newData.products.length === 10 && morePage + 1 < newData.totalPages
				)
			} else {
				setHasMoreProducts(false)
			}
		} catch (error) {
			Toast({ type: 'error', message: 'Error al cargar más productos' })
		} finally {
			setIsLoadingMore(false)
		}
	}, [isLoadingMore, hasMoreProducts, morePage, productsMore])

	const performAdvancedFilter = useCallback(async () => {
		try {
			setIsLoadingMore(true)

			const response = await advancedFilter({
				limit: 10,
				page: 1,
			})

			if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar productos adicionales',
				})
				return
			}

			const newData = response?.data
			if (newData) {
				setProductsMore(newData)
				setMorePage(1)
				setHasMoreProducts(
					newData.products.length === 10 && newData.totalPages > 1
				)
			}
		} catch (error) {
			Toast({ type: 'error', message: 'Error al cargar productos adicionales' })
		} finally {
			setIsLoadingMore(false)
		}
	}, [])

	const performIntelligentSearch = useCallback(
		async (filters: AppliedFilters, searchTerm = query) => {
			if (lastSearchRef.current === searchKey) return

			cancelPreviousRequest()

			const controller = new AbortController()
			abortControllerRef.current = controller

			try {
				setIsLoading(true)
				setError(null)
				lastSearchRef.current = searchKey

				const response = await intelligentSearch({
					searchTerm,
					searchInBrand: true,
					searchInCategory: true,
					searchInDescription: true,
					searchInDetails: true,
					page: currentPage,
					limit: 20,
					...filters,
				})

				if (controller.signal.aborted) return

				if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
					throw new Error('Error al obtener los productos')
				}

				setProducts(response?.data || null)
			} catch (error: any) {
				if (error.name === 'AbortError' || controller.signal.aborted) return

				const errorMessage = 'Error al realizar la búsqueda'
				setError(errorMessage)
				Toast({
					type: 'error',
					message: errorMessage,
				})
			} finally {
				if (!controller.signal.aborted) {
					setIsLoading(false)
				}
			}
		},
		[searchKey, currentPage, cancelPreviousRequest]
	)

	const performQuickSearch = useCallback(
		async (searchTerm: string) => {
			if (!searchTerm.trim()) {
				setProducts(null)
				return
			}

			if (lastSearchRef.current === searchKey) return

			cancelPreviousRequest()

			const controller = new AbortController()
			abortControllerRef.current = controller

			try {
				setIsLoading(true)
				setError(null)
				lastSearchRef.current = searchKey

				const response = await quickSearch(searchTerm, currentPage, 20)

				if (controller.signal.aborted) return

				if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
					throw new Error('Error al obtener los productos')
				}

				setProducts(response?.data || null)
			} catch (error: any) {
				if (error.name === 'AbortError' || controller.signal.aborted) return

				const errorMessage = 'Error al realizar la búsqueda rápida'
				setError(errorMessage)
				Toast({
					type: 'error',
					message: errorMessage,
				})
			} finally {
				if (!controller.signal.aborted) {
					setIsLoading(false)
				}
			}
		},
		[searchKey, currentPage, cancelPreviousRequest]
	)

	const debouncedSearch = useCallback(
		(searchFunction: () => void) => {
			cancelPreviousRequest()

			searchTimeoutRef.current = setTimeout(() => {
				searchFunction()
			}, 300)
		},
		[cancelPreviousRequest]
	)

	useEffect(() => {
		const hasAdvancedFilters = categoryId || minPrice || maxPrice

		if (hasAdvancedFilters) {
			const filters: AppliedFilters = {
				categoryId: categoryId || undefined,
				minPrice: minPrice ? parseInt(minPrice) : undefined,
				maxPrice: maxPrice ? parseInt(maxPrice) : undefined,
			}
			debouncedSearch(() => performIntelligentSearch(filters))
		} else if (query) {
			debouncedSearch(() => performQuickSearch(query))
		} else {
			debouncedSearch(() => performIntelligentSearch({}))
		}

		performAdvancedFilter()

		return () => {
			cancelPreviousRequest()
		}
	}, [
		query,
		categoryId,
		minPrice,
		maxPrice,
		currentPage,
		performIntelligentSearch,
		performQuickSearch,
		debouncedSearch,
		cancelPreviousRequest,
		performAdvancedFilter,
	])

	const handleSortChange = useCallback(
		(newSort: SortOption) => {
			setSortBy(newSort)

			if (products?.products) {
				const sortedProducts = [...products.products].sort((a, b) => {
					switch (newSort) {
						case 'price-asc':
							return (
								(a.priceInfo?.finalPrice || 0) - (b.priceInfo?.finalPrice || 0)
							)
						case 'price-desc':
							return (
								(b.priceInfo?.finalPrice || 0) - (a.priceInfo?.finalPrice || 0)
							)
						case 'name':
							return a.product.name.localeCompare(b.product.name)
						case 'rating':
							const avgA = a.statistics?.averageRating || 0
							const avgB = b.statistics?.averageRating || 0
							return avgB - avgA
						case 'newest':
							const dateA = new Date(a.product._created || 0).getTime()
							const dateB = new Date(b.product._created || 0).getTime()
							return dateB - dateA
						default:
							return (b.relevanceScore || 0) - (a.relevanceScore || 0)
					}
				})

				setProducts({
					...products,
					products: sortedProducts,
				})
			}
		},
		[products]
	)

	const handleViewModeChange = useCallback(
		(mode: ViewMode) => {
			setViewMode(mode)
			const newParams = new URLSearchParams(searchParams)
			newParams.set('view', mode)
			setSearchParams(newParams, { replace: true })
		},
		[searchParams, setSearchParams]
	)

	const handleRetry = useCallback(() => {
		setError(null)
		lastSearchRef.current = ''

		const hasAdvancedFilters = categoryId || minPrice || maxPrice
		if (hasAdvancedFilters) {
			const filters: AppliedFilters = {
				categoryId: categoryId || undefined,
				minPrice: minPrice ? parseInt(minPrice) : undefined,
				maxPrice: maxPrice ? parseInt(maxPrice) : undefined,
			}
			performIntelligentSearch(filters)
		} else if (query) {
			performQuickSearch(query)
		} else {
			performIntelligentSearch({})
		}
	}, [
		categoryId,
		minPrice,
		maxPrice,
		query,
		performIntelligentSearch,
		performQuickSearch,
	])

	const handleClearSearch = useCallback(() => {
		setSearchParams({})
		navigate('/search')
		setProducts(null)
		setError(null)
		lastSearchRef.current = ''
	}, [setSearchParams, navigate])

	const handleNavigateHome = useCallback(() => {
		navigate('/')
	}, [navigate])

	useEffect(() => {
		const savedViewMode = searchParams.get('view') as ViewMode
		if (savedViewMode && ['grid', 'list'].includes(savedViewMode)) {
			setViewMode(savedViewMode)
		}
	}, [searchParams])

	useEffect(() => {
		return () => {
			cancelPreviousRequest()
		}
	}, [cancelPreviousRequest])

	const productCount = products?.totalCount || 0
	const hasQuery = Boolean(query)
	const hasCategoryFilter = Boolean(categoryId)
	const hasResults = products && products.products.length > 0
	const showEmptyState =
		!isLoading && !error && (!products || products.products.length === 0)
	const shouldShowMoreProducts =
		productsMore && productsMore.products.length > 0

	return (
		<Layout customer={customer} token={token}>
			<div className='bg-gradient-to-b from-gray-100 to-white min-h-screen'>
				<div className='bg-white border-b border-gray-200 shadow-sm'>
					<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
						<div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6'>
							<div className='flex-1'>
								<div className='flex items-center gap-3 mb-2'>
									<SearchIcon size={24} className='text-red-500' />
									<h1 className='text-2xl lg:text-3xl font-bold text-gray-900'>
										{hasQuery
											? `Resultados para "${query}"`
											: 'Explorar productos'}
									</h1>
								</div>
								<div className='flex flex-wrap items-center gap-4 text-sm text-gray-600'>
									<span className='flex items-center gap-1'>
										<span className='font-semibold text-red-600'>
											{productCount}
										</span>
										productos encontrados
									</span>
									{hasQuery && (
										<span className='px-2 py-1 bg-red-50 text-red-700 rounded-md font-medium'>
											Búsqueda: {query}
										</span>
									)}
									{hasCategoryFilter && (
										<span className='px-2 py-1 bg-gray-100 text-gray-700 rounded-md font-medium'>
											Categoría filtrada
										</span>
									)}
								</div>
							</div>

							<div className='flex items-center gap-4'>
								<div className='flex items-center gap-2'>
									<span className='text-sm font-medium text-gray-700 hidden sm:block'>
										Ordenar:
									</span>
									<div className='relative'>
										<select
											value={sortBy}
											onChange={(e) =>
												handleSortChange(e.target.value as SortOption)
											}
											className='appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent hover:border-gray-400 transition-colors min-w-[160px]'
											disabled={isLoading}
										>
											{sortOptions.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
										<ChevronDown
											size={16}
											className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none'
										/>
									</div>
								</div>

								<div className='flex items-center bg-gray-100 rounded-lg p-1'>
									<button
										onClick={() => handleViewModeChange('list')}
										className={`p-2 rounded-md transition-all duration-200 ${
											viewMode === 'list'
												? 'bg-red-500 text-white shadow-sm'
												: 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
										}`}
										title='Vista de lista'
										disabled={isLoading}
									>
										<List size={18} />
									</button>
									<button
										onClick={() => handleViewModeChange('grid')}
										className={`p-2 rounded-md transition-all duration-200 ${
											viewMode === 'grid'
												? 'bg-red-500 text-white shadow-sm'
												: 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
										}`}
										title='Vista de cuadrícula'
										disabled={isLoading}
									>
										<Grid size={18} />
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
					{isLoading && <ProductCardSkeletons count={8} viewMode={viewMode} />}

					{error && !isLoading && (
						<div className='flex flex-col items-center justify-center py-20'>
							<div className='bg-red-50 p-6 rounded-xl text-center max-w-md'>
								<AlertCircle size={48} className='text-red-500 mx-auto mb-4' />
								<h3 className='text-lg font-semibold text-gray-900 mb-2'>
									Error en la búsqueda
								</h3>
								<p className='text-gray-600 mb-4'>
									{error || 'Ocurrió un error al buscar productos'}
								</p>
								<button
									onClick={handleRetry}
									className='px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium'
								>
									Intentar de nuevo
								</button>
							</div>
						</div>
					)}

					{showEmptyState && (
						<div className='flex flex-col items-center justify-center py-20'>
							<div className='text-center max-w-md'>
								<SearchIcon size={64} className='text-gray-300 mx-auto mb-4' />
								<h3 className='text-xl font-semibold text-gray-900 mb-2'>
									No se encontraron productos
								</h3>
								<p className='text-gray-600 mb-6'>
									{hasQuery
										? `No hay resultados para "${query}". Intenta con otros términos de búsqueda.`
										: 'No hay productos disponibles en este momento.'}
								</p>
								<div className='space-y-3'>
									<button
										onClick={handleClearSearch}
										className='w-full px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium'
									>
										Ver todos los productos
									</button>
									<button
										onClick={handleNavigateHome}
										className='w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium'
									>
										Volver al inicio
									</button>
								</div>
							</div>
						</div>
					)}

					{hasResults && !isLoading && !error && (
						<div
							className={`${
								viewMode === 'grid'
									? 'grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-6'
									: 'space-y-4'
							}`}
						>
							{products.products.map((filteredProduct) => (
								<ProductCardSearch
									click={() =>
										navigate(
											`/Producto/${filteredProduct.product?.name.replace(
												/ /g,
												'-'
											)}/${filteredProduct.product._id}`
										)
									}
									key={`${filteredProduct.product._id}-${viewMode}`}
									product={filteredProduct.product}
									className={viewMode === 'grid' ? 'h-full' : ''}
								/>
							))}
						</div>
					)}
				</div>

				{shouldShowMoreProducts && (
					<div className='bg-gradient-to-r from-gray-50 via-white to-gray-50 border-t border-gray-200'>
						<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
							<div className='text-center mb-8'>
								<div className='flex items-center justify-center gap-3 mb-4'>
									<div className='bg-gradient-to-r from-red-500 to-pink-500 p-2 rounded-lg'>
										{hasQuery || showEmptyState ? (
											<Sparkles size={24} className='text-white' />
										) : (
											<TrendingUp size={24} className='text-white' />
										)}
									</div>
									<div>
										<h2 className='text-2xl lg:text-3xl font-bold text-gray-900'>
											{hasQuery || showEmptyState
												? 'Tambien te puede interesar'
												: 'Productos destacados'}
										</h2>
										<p className='text-gray-600 mt-1'>
											{hasQuery || showEmptyState
												? 'Descubre otros productos que podrían gustarte'
												: 'Los mejores productos seleccionados para ti'}
										</p>
									</div>
								</div>
							</div>

							<div className='relative'>
								<ProductSimpleSlider
									filteredProducts={productsMore}
									isLoading={isLoadingMore}
									enableInfiniteScroll={true}
									onLoadMore={loadMoreProducts}
									customerId={customer?._id || ''}
									customer={customer}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</Layout>
	)
}

export default Search
